import { rgbaToHex } from "./convert";

/**
 * Normalized color value interface for consistent handling
 */
export interface NormalizedColorValue {
  hex: string;
  original: string;
  isValid: boolean;
}

/**
 * Normalizes hex color values by adding # prefix if missing
 */
export const normalizeHexValue = (value: string): string => {
  const trimmedValue = value.trim();

  // If it's already a hex color with #, return as is
  if (trimmedValue.startsWith("#")) {
    return trimmedValue;
  }

  // Check if it's a valid hex color without #
  // includes 4 and 8 digit hex colors with alpha and 3 and 6 digit hex colors without alpha
  const hexPattern =
    /^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{4}$|^[0-9a-fA-F]{6}$|^[0-9a-fA-F]{8}$/;
  if (hexPattern.test(trimmedValue)) {
    return `#${trimmedValue}`;
  }

  // Return original value if it's not a hex color
  return trimmedValue;
};

/**
 * Parses RGB/RGBA string and converts to hex
 */
export const parseRgbaToHex = (rgbaString: string): string => {
  const cleanedString = rgbaString
    .replace("rgba(", "")
    .replace("rgb(", "")
    .replace(")", "")
    .replace(/,/g, " ")
    .trim();

  const values = cleanedString.split(/\s+/);

  if (values.length >= 3) {
    return rgbaToHex({
      r: Number(values[0]),
      g: Number(values[1]),
      b: Number(values[2]),
      a: Number(values[3]) || 1,
    });
  }

  return rgbaString; // Return original if parsing fails
};

/**
 * Extracts color value from Tailwind color class and converts to hex
 */
export const extractColorFromTailwindClass = (
  value: string,
  comboboxOptions: Array<{ tailwind: string; css: string }>
): string => {
  // Handle Tailwind classes like bg-red-500, text-blue-300, etc.
  if (
    value.startsWith("bg-") ||
    (value.includes("-") && value.split("-").length === 2)
  ) {
    const targetClass = value.startsWith("bg-") ? value : `bg-${value}`;
    const equivalentColor = comboboxOptions.find(
      (option) => option.tailwind === targetClass
    );

    if (equivalentColor) {
      return extractColorFromCssProperty(equivalentColor.css);
    }
  }

  return value;
};

/**
 * Extracts color value from CSS property string like "background-color: #ff0000;"
 */
export const extractColorFromCssProperty = (css: string): string => {
  if (
    css.startsWith("background-color: ") ||
    css.startsWith("color: ") ||
    css.startsWith("border-color: ")
  ) {
    const colorValue = css
      .replace(/^(background-color|color|border-color):\s*/, "")
      .replace(/;$/, "")
      .trim();

    if (colorValue.startsWith("rgb")) {
      return parseRgbaToHex(colorValue);
    }

    return normalizeHexValue(colorValue);
  }

  return css;
};

/**
 * Handles CSS custom properties (variables starting with --)
 */
export const extractColorFromCssVariable = (value: string): string => {
  if (value.startsWith("--")) {
    const cleanedValue = value.replace(/-/g, "");

    if (cleanedValue.startsWith("rgb")) {
      return parseRgbaToHex(cleanedValue);
    }

    return normalizeHexValue(cleanedValue);
  }

  return value;
};

/**
 * Comprehensive color value parser that handles all supported formats
 */
export const parseColorValue = (
  value: string,
  comboboxOptions: Array<{ tailwind: string; css: string }> = []
): NormalizedColorValue => {
  if (!value) {
    return { hex: "#000000", original: value, isValid: false };
  }

  let processedValue = value;

  // Handle CSS custom properties
  if (value.startsWith("--")) {
    processedValue = extractColorFromCssVariable(value);
  }
  // Handle Tailwind classes
  else if (
    value.startsWith("bg-") ||
    (value.includes("-") && value.split("-").length === 2)
  ) {
    processedValue = extractColorFromTailwindClass(value, comboboxOptions);
  }
  // Handle CSS properties
  else if (value.includes(":")) {
    processedValue = extractColorFromCssProperty(value);
  }
  // Handle RGB/RGBA values
  else if (value.startsWith("rgb")) {
    processedValue = parseRgbaToHex(value);
  }
  // Handle hex values
  else {
    processedValue = normalizeHexValue(value);
  }

  // Validate final hex value
  const hexPattern =
    /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
  const isValid = hexPattern.test(processedValue);

  return {
    hex: isValid ? processedValue : "#000000",
    original: value,
    isValid,
  };
};

/**
 * Removes gradient-specific CSS variables from color values
 */
export const cleanGradientVariables = (value: string): string => {
  return value
    .replace("var(--tw-gradient-to-position)", "")
    .replace("var(--tw-gradient-from-position)", "")
    .replace("var(--tw-gradient-via-position)", "")
    .trim();
};

/**
 * Safely extracts color value from CSS property string
 * Replaces duplicate safeExtractValueFromCss functions
 */
export const safeExtractValueFromCss = (css: string): string | null => {
  try {
    const firstCss = css.split("\n")[0];
    let value: string = firstCss.split(":")[1];
    value = value.split(";")[0];
    return value.trim();
  } catch (error) {
    return null;
  }
};

/**
 * Cleans and normalizes color value, preferring direct value over fallback
 * Replaces cleanColorValue logic
 */
export const cleanColorValue = (
  primaryValue?: string,
  fallbackValue?: string
): string => {
  if (primaryValue && primaryValue !== fallbackValue) {
    return primaryValue;
  }
  if (fallbackValue) {
    return fallbackValue;
  }
  return "";
};

/**
 * Extracts position value from CSS or Tailwind option
 * Replaces extractPositionValue function
 */
export const extractPositionValue = (option: {
  css?: string;
  tailwind?: string;
}): string => {
  if (option.css) {
    const match = option.css.match(/:([^;]+);/);
    if (match) return match[1].trim();
  }

  if (option.tailwind) {
    const match = option.tailwind.match(/-(\d+)%/);
    if (match) return `${match[1]}%`;
  }

  return "";
};
